import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Pencil, Trash2 } from "lucide-react";
import { supabase } from "@core/lib/supabase";
import { extractFunctionsErrorMessage } from "../lib/functionsError";
import { Button } from "@core/components/ui/button";
import { TemplateEditor } from "./TemplateEditor";

interface Template {
  id: string;
  name: string;
  primary_color: string;
  secondary_color: string;
  logo_url: string | null;
  created_at: string;
}

export function SettingsAppearance() {
  const { t } = useTranslation(["settings", "common"]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  async function fetchTemplates() {
    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "templates",
        { method: "GET" },
      );
      if (invokeError) throw invokeError;
      setTemplates(data ?? []);
    } catch (err) {
      setError(
        await extractFunctionsErrorMessage(
          err,
          t("settings:errorFetchingTemplates"),
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm(t("settings:confirmDeleteTemplate"))) return;

    try {
      const { error: invokeError } = await supabase.functions.invoke(
        `templates?id=${id}`,
        { method: "DELETE" },
      );

      if (invokeError) {
        setError(
          await extractFunctionsErrorMessage(
            invokeError,
            t("settings:errorDeletingTemplate"),
          ),
        );
        return;
      }

      setTemplates(templates.filter((tpl) => tpl.id !== id));
    } catch (err) {
      setError(t("settings:errorDeletingTemplate"));
    }
  }

  async function handleEditorSave() {
    await fetchTemplates();
    setEditorOpen(false);
    setEditingTemplate(null);
  }

  if (loading) {
    return (
      <p className="text-sm text-muted-foreground">{t("settings:loading")}</p>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-3 text-sm font-medium text-foreground">
          {t("settings:myTemplates")}
        </h3>

        {templates.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("settings:noTemplatesYet")}
          </p>
        ) : (
          <div className="mb-4 space-y-2">
            {templates.map((template) => (
              <div
                key={template.id}
                className="flex items-center justify-between rounded-md border border-border bg-muted/60 px-3 py-2"
              >
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {template.name}
                  </p>
                  <div className="mt-1 flex items-center gap-2">
                    <div
                      className="h-4 w-4 rounded border border-border"
                      style={{ background: template.primary_color }}
                    />
                    <div
                      className="h-4 w-4 rounded border border-border"
                      style={{ background: template.secondary_color }}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingTemplate(template);
                      setEditorOpen(true);
                    }}
                    data-testid={`appearance-template-edit-${template.id}`}
                  >
                    <Pencil className="mr-1 h-3.5 w-3.5" />
                    {t("common:edit")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(template.id)}
                    className="text-destructive hover:text-destructive/80"
                    data-testid={`appearance-template-delete-${template.id}`}
                  >
                    <Trash2 className="mr-1 h-3.5 w-3.5" />
                    {t("common:delete")}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Button
          onClick={() => {
            setEditingTemplate(null);
            setEditorOpen(true);
          }}
          data-testid="appearance-new-template"
        >
          {t("settings:newTemplate")}
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {editorOpen && (
        <TemplateEditor
          template={editingTemplate}
          onSave={handleEditorSave}
          onClose={() => {
            setEditorOpen(false);
            setEditingTemplate(null);
          }}
        />
      )}
    </div>
  );
}
